import { CompressOutlined, PictureOutlined } from '@ant-design/icons';
import { Tooltip } from 'antd';
import React, { FC, memo, useCallback } from 'react';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { InputNumber } from 'shared/ui/InputNumber';
import { getSnapStepPixels } from '../../model/selectors/getSnapStepPixels';
import { warehouseMapConstructorActions } from '../../model/slice/warehouseMapConstructorSlice';
import { WarehouseMapTool } from '../../model/types';
import { ReactComponent as ArrowIcon } from 'shared/assets/icons/ArrowIcon.svg';
import { ReactComponent as PencilIcon } from 'shared/assets/icons/PencilIcon.svg';
import { ReactComponent as RectangleIcon } from 'shared/assets/icons/RectangleIcon.svg';
import { ReactComponent as LineIcon } from 'shared/assets/icons/LineIcon.svg';
import { ReactComponent as CircleIcon } from 'shared/assets/icons/CircleIcon.svg';
import { ReactComponent as GrabIcon } from 'shared/assets/icons/GrabIcon.svg';
import { ReactComponent as TextIcon } from 'shared/assets/icons/TextIcon.svg';
import { ReactComponent as PolygonIcon } from 'shared/assets/icons/PolygonIcon.svg';
import { ReactComponent as PolygonBoxIcon } from 'shared/assets/icons/PolygonBoxIcon.svg';
import { Tool } from './Tool';

interface ToolBarProps {
  activeTool: WarehouseMapTool;
  onChange: (tool: WarehouseMapTool) => void;
  hasAvailableSizeCodes: boolean;
  onFitToContent: () => void;
}

const SIZE_CODE_REQUIRED_TOOLS = new Set<WarehouseMapTool>([WarehouseMapTool.RECTANGLE, WarehouseMapTool.POLYGON_BOX]);

const EditBackgroundIcon: FC<{ className?: string }> = ({ className }) => (
  <PictureOutlined className={`${className} [&>svg]:h-6 [&>svg]:w-6`} />
);

export const ToolBar: FC<ToolBarProps> = memo((props) => {
  const { activeTool, onChange, hasAvailableSizeCodes, onFitToContent } = props;

  const { t } = useAppTranslation('warehouse-management');
  const dispatch = useAppDispatch();

  const snapStepPixels = useAppSelector(getSnapStepPixels);

  const noSizeCodesTitle = t('No size codes available — boxes cannot be drawn');

  const tools = [
    { id: WarehouseMapTool.POINTER, Icon: ArrowIcon },
    { id: WarehouseMapTool.GRAB, Icon: GrabIcon },
    {
      id: WarehouseMapTool.EDIT_BACKGROUND,
      Icon: EditBackgroundIcon,
      label: t('Edit background'),
    },
    {
      id: WarehouseMapTool.RECTANGLE,
      Icon: RectangleIcon,
      label: t('Rectangle'),
      multiTools: [
        { id: WarehouseMapTool.LINE, Icon: LineIcon, label: t('Line') },
        { id: WarehouseMapTool.CIRCLE, Icon: CircleIcon, label: t('Ellipse') },
        { id: WarehouseMapTool.POLYGON, Icon: PolygonIcon, label: t('Polygon') },
        { id: WarehouseMapTool.POLYGON_BOX, Icon: PolygonBoxIcon, label: t('Polygon box') },
      ],
    },
    { id: WarehouseMapTool.PENCIL, Icon: PencilIcon },
    { id: WarehouseMapTool.TEXT, Icon: TextIcon },
  ];

  const changeSnapStepPixels = useCallback(
    (value: Nullable<number>) => {
      if (value != null && value >= 1) {
        dispatch(warehouseMapConstructorActions.changeSnapStepPixels(value));
      }
    },
    [dispatch],
  );

  const snapStepLabel = t('Step');

  return (
    <div className="absolute z-10 bottom-4 left-1/2 -translate-x-1/2 flex space-x-3.5 items-center p-3 w-fit rounded-lg bg-white">
      {tools.map(({ id, Icon, label, multiTools }) => (
        <Tool
          key={id}
          id={id}
          Icon={Icon}
          label={label}
          activeTool={activeTool}
          multiTools={multiTools}
          onClick={onChange}
          disabledTools={hasAvailableSizeCodes ? undefined : SIZE_CODE_REQUIRED_TOOLS}
          disabledTitle={noSizeCodesTitle}
        />
      ))}
      <div className="flex items-center gap-1.5 pl-2 border-l border-neutral-200" title={snapStepLabel}>
        <span className="text-xs text-neutral-600 whitespace-nowrap max-w-[9rem] truncate">{snapStepLabel}</span>
        <InputNumber className="!w-14" bordered size="small" min={1} value={snapStepPixels} onChange={changeSnapStepPixels} />
      </div>
      <div className="flex items-center gap-1.5 pl-2 border-l border-neutral-200">
        <Tooltip title={t('Fit to content')}>
          <CompressOutlined
            className="cursor-pointer text-primary text-lg p-1 rounded-md hover:bg-secondaryAccent transition"
            onClick={onFitToContent}
          />
        </Tooltip>
      </div>
    </div>
  );
});
